import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput } from 'react-native';
import { useFocusEffect, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../src/api/client';
import { SMS_UI_ENABLED } from '../../src/config/features';
import Card from '../../src/components/Card';
import Button from '../../src/components/Button';
import ContentContainer from '../../src/components/ContentContainer';
import InlineConfirm from '../../src/components/InlineConfirm';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';

type Segment = 'all' | 'subscribers' | 'lapsed_30';
interface Audience {
  all: number; subscribers: number; lapsed_30: number;
  smsConfigured?: boolean;
  smsEligible?: { all: number; subscribers: number; lapsed_30: number };
}

const SEGMENTS: Array<{ key: Segment; label: string; desc: string }> = [
  { key: 'all', label: 'All customers', desc: 'Everyone who has paid at your locations' },
  { key: 'subscribers', label: 'Subscribers', desc: 'Customers with an active plan' },
  { key: 'lapsed_30', label: "Haven't visited in 30 days", desc: 'Win them back' },
];

export default function OwnerBroadcastScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();

  // Direct-message mode: /owner/broadcast?userId=...&name=... messages one customer.
  const params = useLocalSearchParams<{ userId?: string; name?: string }>();
  const directUserId = typeof params.userId === 'string' && params.userId ? params.userId : null;
  const directName = typeof params.name === 'string' && params.name ? params.name : 'this customer';

  const [audience, setAudience] = useState<Audience | null>(null);
  const [segment, setSegment] = useState<Segment>('all');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useFocusEffect(useCallback(() => {
    if (directUserId) return;
    api<Audience>('/api/owner/broadcast/audience').then(setAudience).catch(() => {});
  }, [directUserId]));

  const count = directUserId ? 1 : (audience ? audience[segment] : 0);

  const send = async () => {
    setConfirm(false);
    setSending(true);
    try {
      const channels = { email: true, push: true, sms: smsEnabled };
      const payload: any = directUserId
        ? { userId: directUserId, subject: subject.trim(), body: body.trim(), channels }
        : { segment, subject: subject.trim(), body: body.trim(), channels };
      if (smsEnabled && smsBody.trim()) payload.smsBody = smsBody.trim();
      const res = await api<{ emailed: number; pushed: number; texted?: number; total: number }>('/api/owner/broadcast', {
        method: 'POST',
        body: payload,
      });
      toast.show({ type: 'success', title: 'Sent', message: directUserId
        ? `Message sent to ${directName}.`
        : `Emailed ${res.emailed}${smsEnabled ? `, texted ${res.texted ?? 0}` : ''} of ${res.total} customer${res.total !== 1 ? 's' : ''}.` });
      setSubject(''); setBody(''); setSmsBody('');
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSending(false); }
  };

  const canSend = subject.trim().length > 0 && body.trim().length > 0 && count > 0;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer maxWidth={640}>
        <Card style={{ marginBottom: spacing.md }}>
          <Text style={styles.title}>Message customers</Text>
          <Text style={styles.subtitle}>Send a one-off announcement by email and push (e.g. closures, specials). No voucher — for offers use Winback.</Text>
        </Card>

        <Card style={{ marginBottom: spacing.md }}>
          <Text style={styles.sectionTitle}>Audience</Text>
          {directUserId ? (
            <View style={[styles.segment, styles.segmentActive]}>
              <Ionicons name="person" size={20} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.segmentLabel, { color: colors.primary }]}>{directName}</Text>
                <Text style={styles.segmentDesc}>Direct message to one customer</Text>
              </View>
              <Text style={styles.segmentCount}>1</Text>
            </View>
          ) : SEGMENTS.map(s => {
            const active = segment === s.key;
            const n = audience ? audience[s.key] : null;
            return (
              <TouchableOpacity key={s.key} onPress={() => setSegment(s.key)} style={[styles.segment, active && styles.segmentActive]}
                accessibilityRole="radio" accessibilityState={{ selected: active }}>
                <View style={[styles.radio, active && styles.radioActive]}>{active && <View style={styles.radioDot} />}</View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.segmentLabel, active && { color: colors.primary }]}>{s.label}</Text>
                  <Text style={styles.segmentDesc}>{s.desc}</Text>
                </View>
                <Text style={styles.segmentCount}>{n == null ? '…' : n}</Text>
              </TouchableOpacity>
            );
          })}
        </Card>

        <Card style={{ marginBottom: spacing.md }}>
          <Text style={styles.sectionTitle}>Message</Text>
          <Text style={styles.fieldLabel}>Subject</Text>
          <TextInput style={styles.input} value={subject} onChangeText={setSubject} maxLength={200}
            placeholder="Weekend special — $3 off Ultimate" placeholderTextColor={colors.textLight} />
          <Text style={styles.fieldLabel}>Body</Text>
          <TextInput style={[styles.input, styles.bodyInput]} value={body} onChangeText={setBody} maxLength={5000} multiline
            placeholder="Write your message…" placeholderTextColor={colors.textLight} />
        </Card>

        {SMS_UI_ENABLED && audience?.smsConfigured && !directUserId && (
          <Card style={{ marginBottom: spacing.md }}>
            <TouchableOpacity onPress={() => setSmsEnabled(v => !v)} style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <View style={[styles.radio, smsEnabled && styles.radioActive, { borderRadius: 4 }]}>
                {smsEnabled && <Ionicons name="checkmark" size={14} color={colors.primary} />}
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.segmentLabel}>Also send as text message</Text>
                <Text style={styles.segmentDesc}>
                  {audience.smsEligible ? `${audience.smsEligible[segment]} customer${audience.smsEligible[segment] !== 1 ? 's' : ''} in this segment opted in to texts` : 'Only customers who opted in receive texts'}
                </Text>
              </View>
            </TouchableOpacity>
            {smsEnabled && (
              <>
                <Text style={{ fontSize: fontSize.xs, color: colors.warning, marginTop: spacing.sm }}>
                  Additional charge applies: ~1¢ per text, billed weekly at actual carrier cost.
                </Text>
                <Text style={styles.fieldLabel}>Text message (short — leave blank to use subject + body)</Text>
                <TextInput
                  style={styles.input} value={smsBody} onChangeText={setSmsBody} maxLength={280}
                  placeholder="This weekend: $3 off Ultimate washes!"
                  placeholderTextColor={colors.textLight}
                />
                <Text style={styles.segmentDesc}>
                  Sent as "{'{'}Your business{'}'}: message" with an opt-out notice. {280 - smsBody.length} characters left.
                </Text>
              </>
            )}
          </Card>
        )}

        {confirm ? (
          <InlineConfirm
            visible={confirm}
            message={directUserId ? `Send to ${directName}?` : `Send to ${count} customer${count !== 1 ? 's' : ''}?`}
            confirmLabel="Send"
            onConfirm={send}
            onCancel={() => setConfirm(false)}
          />
        ) : (
          <Button
            title={sending ? 'Sending…' : directUserId ? `Send to ${directName}` : `Send to ${count} customer${count !== 1 ? 's' : ''}`}
            onPress={() => setConfirm(true)}
            disabled={!canSend || sending}
            size="lg"
          />
        )}
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  subtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 4 },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },
  segment: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md, borderRadius: borderRadius.md, borderWidth: 2, borderColor: c.border, marginBottom: spacing.sm },
  segmentActive: { borderColor: c.primary, backgroundColor: c.primary + '0D' },
  radio: { width: 20, height: 20, borderRadius: 10, borderWidth: 2, borderColor: c.border, alignItems: 'center', justifyContent: 'center' },
  radioActive: { borderColor: c.primary },
  radioDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: c.primary },
  segmentLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  segmentDesc: { fontSize: fontSize.xs, color: c.textLight, marginTop: 1 },
  segmentCount: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.textSecondary },
  fieldLabel: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: 4, marginTop: spacing.sm },
  input: { borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, padding: spacing.sm, fontSize: fontSize.sm, color: c.text },
  bodyInput: { minHeight: 120, textAlignVertical: 'top' },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
